import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ErrorBoundary } from '@/components/features/ErrorBoundary';
import { ProtectedRoute } from '@/components/features/ProtectedRoute';
import { AdminLayout } from '@/components/layouts/AdminLayout';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { DashboardPage } from '@/pages/admin/DashboardPage';
import { ResourcesPage } from '@/pages/admin/ResourcesPage';
import { ResourceDetailPage } from '@/pages/admin/ResourceDetailPage';
import { ProcedureTypesPage } from '@/pages/admin/ProcedureTypesPage';
import { PatientsPage } from '@/pages/admin/PatientsPage';
import { SchedulePage } from '@/pages/admin/SchedulePage';
import { DailyPanelPage } from '@/pages/admin/DailyPanelPage';
import { CampaignsPage } from '@/pages/admin/CampaignsPage';
import { CampaignDetailPage } from '@/pages/admin/CampaignDetailPage';
import { PoliciesPage } from '@/pages/admin/PoliciesPage';
import { PatientJourneyPage } from '@/pages/admin/PatientJourneyPage';
import { InvitationsPage } from '@/pages/admin/InvitationsPage';
import { LeadsPage } from '@/pages/admin/LeadsPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { IntegrationDashboardPage } from '@/pages/admin/IntegrationDashboardPage';
import { PatientScoresPage } from '@/pages/admin/PatientScoresPage';
import { PatientLtvPage } from '@/pages/admin/PatientLtvPage';
import { LeadImportsPage } from '@/pages/admin/LeadImportsPage';
import { LeadProvidersPage } from '@/pages/admin/LeadProvidersPage';
import { PatientRegisterPage } from '@/pages/portal/PatientRegisterPage';
import { InvitationPage } from '@/pages/portal/InvitationPage';
import { PatientSchedulingPage } from '@/pages/portal/PatientSchedulingPage';
import { PatientDashboardPage } from '@/pages/portal/PatientDashboardPage';
import { CampaignWidgetPage } from '@/pages/widget/CampaignWidgetPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SystemOverviewPage } from '@/pages/SystemOverviewPage';
import type { UserRole } from '@/types';

/** Admin + Receptionist can access operational routes. */
const STAFF_ROLES: UserRole[] = ['Admin', 'Receptionist'];
/** Only Admin can access system configuration routes. */
const ADMIN_ONLY: UserRole[] = ['Admin'];

/**
 * Root application component.
 *
 * Route structure:
 * - Public: /, /login, /register, /forgot-password
 * - Widget: /widget/campaign/:slug (standalone, no layout)
 * - Portal (public): /portal/register, /portal/invite/:token
 * - Portal (patient auth): /portal/schedule, /portal/dashboard
 * - Admin (Admin+Receptionist): /admin/dashboard, patients, schedule, daily-panel, etc.
 * - Admin (Admin only): /admin/users, integrations, policies
 *
 * Patients trying to access /admin/* are redirected to /portal/dashboard.
 */
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <ErrorBoundary>
              <BrowserRouter>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                  {/* System overview (moved to admin area) */}

                  {/* Widget routes (standalone, no layout) */}
                  <Route path="/widget/campaign/:slug" element={<CampaignWidgetPage />} />

                  {/* Patient portal routes (public) */}
                  <Route path="/portal/register" element={<PatientRegisterPage />} />
                  <Route path="/portal/invite/:token" element={<InvitationPage />} />

                  {/* Patient portal routes (authenticated) */}
                  <Route
                    path="/portal/schedule"
                    element={
                      <ProtectedRoute>
                        <PatientSchedulingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/portal/dashboard"
                    element={
                      <ProtectedRoute>
                        <PatientDashboardPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin redirect */}
                  <Route
                    path="/admin"
                    element={<Navigate to="/admin/dashboard" replace />}
                  />

                  {/* Staff routes (Admin + Receptionist) */}
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <DashboardPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/patients"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <PatientsPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/patient-scores"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <PatientScoresPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/ltv"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <PatientLtvPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/schedule"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <SchedulePage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/daily-panel"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <DailyPanelPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/campaigns"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <CampaignsPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/campaigns/:id"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <CampaignDetailPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/leads"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <LeadsPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/invitations"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <InvitationsPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/patient-journeys/:id"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <PatientJourneyPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/resources"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <ResourcesPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/resources/:id"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <ResourceDetailPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/procedure-types"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <ProcedureTypesPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin-only routes (system configuration) */}
                  <Route
                    path="/admin/policies"
                    element={
                      <ProtectedRoute allowedRoles={ADMIN_ONLY}>
                        <AdminLayout>
                          <PoliciesPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute allowedRoles={ADMIN_ONLY}>
                        <AdminLayout>
                          <UsersPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/integrations"
                    element={
                      <ProtectedRoute allowedRoles={ADMIN_ONLY}>
                        <AdminLayout>
                          <IntegrationDashboardPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/lead-imports"
                    element={
                      <ProtectedRoute allowedRoles={ADMIN_ONLY}>
                        <AdminLayout>
                          <LeadImportsPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/lead-providers"
                    element={
                      <ProtectedRoute allowedRoles={ADMIN_ONLY}>
                        <AdminLayout>
                          <LeadProvidersPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/overview"
                    element={
                      <ProtectedRoute allowedRoles={STAFF_ROLES}>
                        <AdminLayout>
                          <SystemOverviewPage />
                        </AdminLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch-all */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </BrowserRouter>
            </ErrorBoundary>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
